import type { MembershipClearance, MembershipRow } from '../api';

/**
 * Раскладка списка персонала: колонки оснований допуска и подписи в клетках.
 *
 * Здесь ТОЛЬКО оформление. Что просрочено, что истекает и чего нет вовсе —
 * уже решил сервер тем же расчётом, которым считает готовность бригады
 * (`apps/api/src/clearances/clearances.logic.ts`). Панель не сравнивает даты
 * и не решает, годится ли работник: второй ответ на этот вопрос разошёлся бы
 * с первым при первой же правке одного из них.
 *
 * Вынесено из разметки отдельным модулем, потому что проверяется: тестов
 * компонентов в окружении панели нет, а подписи в клетках — это то, что
 * человек читает перед тем, как поставить работника в бригаду.
 */

/** `2026-10-05` → `05.10.2026`. Не дата — строка как пришла. */
export function formatDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
}

/**
 * Подпись в клетке основания допуска.
 *
 * Три состояния названы словами, а не цветом: цвет не читается при печати и
 * не читается тем, кто его не различает. «Просрочен» и «нет записи» —
 * разные подписи намеренно: в первом случае документ был и его продлевают,
 * во втором его не было никогда.
 *
 * Четвёртая подпись — «группа по ЭБ не указана» (issue #520): запись есть и
 * срок у неё действует, но у допуска по электробезопасности содержание
 * документа — это номер группы, и без него запись не удостоверяет ничего.
 * Решил это СЕРВЕР (признак `electricalGroupMissing`), тем же расчётом,
 * которым не пускает работника наряд; панель называет его словами. Стоит
 * первой: срок документа, в котором нет главного, называть незачем — от него
 * пошли бы выдавать новый вместо того, чтобы дописать группу в этот.
 */
export function clearanceCell(clearance: MembershipClearance | undefined): string {
  // Строка без этой колонки — колонки собраны из тех же ответов, так что это
  // означало бы расхождение внутри одного ответа. Прочерк здесь читается как
  // «не заполнено», и это ближе всего к правде.
  if (!clearance) return '—';
  if (clearance.electricalGroupMissing) return 'группа по ЭБ не указана';
  const group = clearance.electricalSafetyGroup === null ? '' : `группа ${clearance.electricalSafetyGroup}, `;
  const date = clearance.expiresAt === null ? null : formatDate(clearance.expiresAt);
  switch (clearance.status) {
    case 'missing':
      return 'нет записи';
    case 'expired':
      return date === null ? `${group}просрочен` : `${group}просрочен ${date}`;
    case 'expiring_soon':
      return date === null ? `${group}истекает` : `${group}истекает ${date}`;
    default:
      return date === null ? `${group}бессрочно` : `${group}до ${date}`;
  }
}

/**
 * Итог по работнику — тот же светофор, что отвечает на вопрос «допущен ли он
 * к работе», словами.
 */
export function admissionLabel(row: Pick<MembershipRow, 'overallStatus'>): string {
  switch (row.overallStatus) {
    case 'admitted':
      return 'Допущен';
    case 'warning':
      return 'Истекают сроки';
    default:
      return 'Не допущен';
  }
}

export function admissionClass(row: Pick<MembershipRow, 'overallStatus'>): string | undefined {
  switch (row.overallStatus) {
    case 'warning':
      return 'clearance clearance--expiring';
    case 'not_admitted':
      return 'clearance clearance--expired';
    default:
      return undefined;
  }
}

/**
 * Подразделение работника.
 *
 * Прочерк значит «работник не приписан к подразделению» — так же, как в
 * самом бланке. Права на структуру организации это больше не касается:
 * название приходит вместе со строкой работника.
 */
export function subdivisionCell(row: Pick<MembershipRow, 'subdivisionId' | 'subdivisionName'>): string {
  if (row.subdivisionId === null) return '—';
  return row.subdivisionName ?? 'название не загрузилось';
}

// ─── Экран «за секунду» ───────────────────────────────────────────────────
//
// Всё ниже — оформление и порядок. Состояние работника и каждого допуска
// по-прежнему решает сервер (`clearances.logic.ts`): что просрочено, что
// истекает, допущен ли человек. Здесь из готового ответа собирается то, что
// инженер по охране труда видит глазом, не читая таблицу построчно.

/**
 * Тон строки — тот же светофор, что в мобильном приложении
 * (`overallTone` в `apps/mobile/src/components/ClearanceStatusBadge.tsx`):
 * допущен — зелёный, истекают сроки — жёлтый, не допущен — красный.
 *
 * Пятого цвета нет, но есть четвёртое состояние: уволенный или отключённый
 * работник. Красить его красным неверно — это крик о человеке, который уже
 * не работает и которого в бригаду никто не поставит. Он серый и внизу.
 */
export type RowTone = 'danger' | 'warn' | 'ok' | 'inactive';

export function rowTone(row: Pick<MembershipRow, 'isActive' | 'overallStatus'>): RowTone {
  if (!row.isActive) return 'inactive';
  switch (row.overallStatus) {
    case 'admitted':
      return 'ok';
    case 'warning':
      return 'warn';
    default:
      return 'danger';
  }
}

/**
 * Колонки, по которым принимают решение: медосмотр, повторный инструктаж,
 * группа по электробезопасности.
 *
 * Находятся по КАТЕГОРИИ И ВИДУ допуска, а не по названию. Название
 * организация пишет как хочет — «Медосмотр», «Медицинский осмотр»,
 * «Предсменный медосмотр», — и поиск по нему однажды промахнулся бы молча:
 * колонка стояла бы пустой у всех, и пустоту прочитали бы как «всё в
 * порядке». Повторный инструктаж — единственный периодический среди
 * инструктажей: вводный и первичный разовые, внеплановый — событие.
 *
 * Ответ сервера без категории (прежняя сборка) даёт `undefined`, а не
 * догадку по названию: на экране это «не заведён», и это правда о том, что
 * известно панели.
 */
export type DecisionKey = 'medical' | 'repeatBriefing' | 'electrical';

const DECISION_MATCH: Record<DecisionKey, (c: MembershipClearance) => boolean> = {
  medical: (c) => c.category === 'MEDICAL',
  repeatBriefing: (c) => c.category === 'BRIEFING' && c.kind === 'PERIODIC',
  electrical: (c) => c.category === 'ELECTRICAL_SAFETY',
};

/**
 * Насколько допуск горит: меньше — срочнее. Нет записи — горит сильнее
 * всего: документа не было никогда. Бессрочный — не горит вовсе.
 */
function urgency(c: MembershipClearance): number {
  // Запись без номера группы по ЭБ не удостоверяет ничего (issue #520) —
  // горит так же, как её отсутствие, и срок у неё не в счёт.
  if (c.status === 'missing' || c.electricalGroupMissing) return Number.NEGATIVE_INFINITY;
  if (c.expiresAt === null) return Number.POSITIVE_INFINITY;
  return Date.UTC(
    Number(c.expiresAt.slice(0, 4)),
    Number(c.expiresAt.slice(5, 7)) - 1,
    Number(c.expiresAt.slice(8, 10)),
  );
}

/**
 * Допуск нужного рода у работника. Если организация завела два вида одного
 * рода (скажем, два медосмотра), берётся тот, что горит сильнее: решение
 * принимают по худшему, а не по первому попавшемуся.
 */
export function decisionClearance(
  row: Pick<MembershipRow, 'clearances'>,
  key: DecisionKey,
): MembershipClearance | undefined {
  const matching = row.clearances.filter(DECISION_MATCH[key]);
  if (matching.length === 0) return undefined;
  return matching.reduce((worst, next) => (urgency(next) < urgency(worst) ? next : worst));
}

/**
 * Что нужно знать о допуске, чтобы назвать его срок словами и окрасить
 * клетку: состояние, срок и признак записи без номера группы по ЭБ.
 *
 * Не `Pick` по строке списка: карточка работника читает другую форму записи
 * (`ClearanceWithStatus`) и собирает эти поля сама.
 */
export type TermSource = {
  status: MembershipClearance['status'];
  expiresAt: string | null;
  electricalGroupMissing?: true;
};

/**
 * Класс клетки срока — тем же светофором, что и слово рядом.
 *
 * Отдельной функцией, а не подстановкой состояния в разметке: у записи без
 * номера группы по ЭБ состояние сервера остаётся `active` (срок-то действует),
 * и клетка, окрашенная по состоянию, была бы зелёной под красной подписью
 * (issue #520).
 */
export function termClass(clearance: Omit<TermSource, 'expiresAt'>): string {
  return `term term--${clearance.electricalGroupMissing ? 'group_missing' : clearance.status}`;
}

/** Календарный день без времени и пояса. */
export type CalendarDay = { year: number; month: number; day: number };

/** Сегодня по часам того, кто смотрит. Только для подсчёта «через сколько». */
export function localToday(now: Date = new Date()): CalendarDay {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

const days = (n: number) => `${n} ${plural(n, 'день', 'дня', 'дней')}`;
const weeks = (n: number) => `${n} ${plural(n, 'неделю', 'недели', 'недель')}`;
const months = (n: number) => `${n} ${plural(n, 'месяц', 'месяца', 'месяцев')}`;
const years = (n: number) => `${n} ${plural(n, 'год', 'года', 'лет')}`;

/**
 * Срок словами: «через 3 недели», «просрочен 32 дня».
 *
 * СЛОВО И ЧИСЛО БЕРУТСЯ ИЗ РАЗНЫХ МЕСТ, И ЭТО НАМЕРЕННО. «Просрочен» или
 * «через» — это состояние, и его решает сервер. Число — сколько дней до даты
 * или после неё — считается здесь, от даты, которую прислал тот же сервер.
 * Решай панель состояние сама, около полуночи экран мог бы спорить с
 * сервером: сервер уже считает допуск просроченным, а панель ещё пишет
 * «через 0 дней».
 *
 * Шкала: просрочку — в днях, потому что она срочна и счёт идёт на дни. До
 * двух недель — дни, до двух месяцев — недели, до года — месяцы, дальше —
 * годы.
 *
 * Дата разбирается текстом, а не через `Date` в местном поясе: полночь UTC
 * при пересчёте уезжает на сутки назад, и допуск, истекающий 23-го,
 * показывался бы истекающим 22-го (тот же довод у `formatClearanceDate` в
 * мобильном приложении).
 */
export function relativeTerm(clearance: TermSource, today: CalendarDay): string {
  // Issue #520: у допуска по электробезопасности без номера группы срока как
  // бы и нет — удостоверять нечему. Слово, как и все остальные здесь, взято у
  // сервера: признак приходит вместе со строкой работника.
  if (clearance.electricalGroupMissing) return 'группа по ЭБ не указана';
  if (clearance.status === 'missing') return 'нет записи';
  if (clearance.expiresAt === null) return 'бессрочно';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(clearance.expiresAt);
  if (!match) return clearance.expiresAt;

  const target = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const base = Date.UTC(today.year, today.month - 1, today.day);
  const diff = Math.round((target - base) / 86_400_000);

  if (clearance.status === 'expired') {
    // Часы смотрящего могут отставать от сервера: сервер уже сказал
    // «просрочен», а по часам ещё не наступило. Спорить с сервером нельзя —
    // слово остаётся его, число не опускается ниже одного дня.
    return `просрочен ${days(Math.max(1, -diff))}`;
  }
  if (diff <= 0) return 'истекает сегодня';
  if (diff < 14) return `через ${days(diff)}`;
  if (diff < 60) return `через ${weeks(Math.round(diff / 7))}`;
  const inMonths = Math.round(diff / 30.44);
  if (inMonths < 12) return `через ${months(Math.max(2, inMonths))}`;
  return `через ${years(Math.max(1, Math.round(diff / 365.25)))}`;
}

/**
 * Порядок строк. По умолчанию — проблемные сверху; «по фамилии» — когда
 * человека ищут глазами по алфавиту, как в бумажном списке.
 */
export type SortOrder = 'problems' | 'name';

const TONE_ORDER: Record<RowTone, number> = { danger: 0, warn: 1, ok: 2, inactive: 3 };

/**
 * Порядок по умолчанию: не допущенные, затем истекающие, затем допущенные,
 * уволенные — в самом низу.
 *
 * Внутри группы выше тот, у кого горит сильнее: нет записи, затем самая
 * ранняя дата. Два просроченных — вверху тот, у кого просрочено раньше: его
 * дольше не допускают, и с ним разбираться первым.
 */
export function sortRows<T extends MembershipRow>(rows: readonly T[], order: SortOrder = 'problems'): T[] {
  const byName = (a: T, b: T) => a.user.name.localeCompare(b.user.name, 'ru');
  if (order === 'name') return [...rows].sort(byName);
  const mostUrgent = (row: T) =>
    row.clearances.reduce((min, c) => Math.min(min, urgency(c)), Number.POSITIVE_INFINITY);
  return [...rows].sort(
    (a, b) =>
      TONE_ORDER[rowTone(a)] - TONE_ORDER[rowTone(b)]
      || mostUrgent(a) - mostUrgent(b)
      || byName(a, b),
  );
}

/**
 * Что показать: всех, только проблемных или один цвет.
 *
 * Уволенные видны только во «всех»: среди «проблемных» им не место — ставить
 * их в бригаду никто не будет, и строка о них отвлекала бы от тех, с кем
 * разбираться сегодня.
 */
/**
 * `noBasis` (issue #537) — работники с действующими допусками без документа-
 * основания. Не цвет строки, а отдельный вопрос: «кому дозаполнить основание».
 */
export type ToneFilter = 'all' | 'problems' | 'danger' | 'warn' | 'ok' | 'noBasis';

/** Сколько у работника действующих допусков без документа-основания (issue #537). */
export function withoutBasisCount(row: Pick<MembershipRow, 'clearancesWithoutBasis'>): number {
  return row.clearancesWithoutBasis ?? 0;
}

/** Сколько работников попадёт в отбор «Допуски без документа». */
export function countWithoutBasis(rows: readonly MembershipRow[]): number {
  return rows.filter((row) => withoutBasisCount(row) > 0).length;
}

/**
 * Отбор по выбору ЧЕЛОВЕКА, а не по правам. Кого вообще видно, решил сервер
 * (`apps/api/src/admin/personnel-visibility.ts`), и здесь это не
 * пересматривается: подразделение в условиях отбора не участвует вовсе.
 * Отбор только сужает уже разрешённый список до того, что человек попросил.
 */
export function filterRows<T extends MembershipRow>(
  rows: readonly T[],
  filter: { tone: ToneFilter; query: string },
): T[] {
  const query = filter.query.trim().toLocaleLowerCase('ru');
  return rows.filter((row) => {
    const tone = rowTone(row);
    const toneOk =
      filter.tone === 'all'
      || (filter.tone === 'problems' && (tone === 'danger' || tone === 'warn'))
      || (filter.tone === 'noBasis' && withoutBasisCount(row) > 0)
      || filter.tone === tone;
    if (!toneOk) return false;
    if (query.length === 0) return true;
    return (
      row.user.name.toLocaleLowerCase('ru').includes(query)
      || (row.employeeNumber ?? '').toLocaleLowerCase('ru').includes(query)
    );
  });
}

/** Счётчики над таблицей: сколько в каждом цвете. Уволенные — отдельно. */
export function countByTone(rows: readonly MembershipRow[]): Record<RowTone, number> {
  const counts: Record<RowTone, number> = { danger: 0, warn: 0, ok: 0, inactive: 0 };
  for (const row of rows) counts[rowTone(row)] += 1;
  return counts;
}
